import React, { useEffect, useState } from 'react';
import { Page, Layout, Card, Text, BlockStack, InlineStack, Button, TextField, Banner } from '@shopify/polaris';
import { authenticatedFetch, useAuth } from '../AuthComponent';

async function jsonOrThrow(resp) {
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.detail || `${resp.status} ${resp.statusText}`);
  return data;
}

function Profile() {
  const { fullName: contextFullName } = useAuth();
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingName, setSavingName] = useState(false);
  const [nameNotice, setNameNotice] = useState('');
  const [nameError, setNameError] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordNotice, setPasswordNotice] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    authenticatedFetch('/api/users/me')
      .then(jsonOrThrow)
      .then(data => setFullName(data.full_name || ''))
      .catch(() => setFullName(contextFullName || ''))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveName = async () => {
    setSavingName(true); setNameError(''); setNameNotice('');
    try {
      await authenticatedFetch('/api/users/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ full_name: fullName }),
      }).then(jsonOrThrow);
      setNameNotice('Saved.');
    } catch (e) {
      setNameError(e.message || String(e));
    } finally {
      setSavingName(false);
    }
  };

  const changePassword = async () => {
    setPasswordError(''); setPasswordNotice('');
    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirmation don't match.");
      return;
    }
    setSavingPassword(true);
    try {
      await authenticatedFetch('/api/users/me/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      }).then(jsonOrThrow);
      setPasswordNotice('Password changed.');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (e) {
      setPasswordError(e.message || String(e));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <Page title="My Profile">
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Full name</Text>
              <TextField label="Full name" value={fullName} onChange={setFullName} autoComplete="off" disabled={loading} />
              {nameError && <Banner tone="critical">{nameError}</Banner>}
              {nameNotice && <Banner tone="success">{nameNotice}</Banner>}
              <InlineStack>
                <Button variant="primary" loading={savingName} disabled={!fullName.trim()} onClick={saveName}>
                  Save
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Change password</Text>
              <TextField
                label="Current password" type="password" value={currentPassword}
                onChange={setCurrentPassword} autoComplete="current-password"
              />
              <TextField
                label="New password" type="password" value={newPassword}
                onChange={setNewPassword} autoComplete="new-password"
                helpText="At least 10 characters."
              />
              <TextField
                label="Confirm new password" type="password" value={confirmPassword}
                onChange={setConfirmPassword} autoComplete="new-password"
              />
              {passwordError && <Banner tone="critical">{passwordError}</Banner>}
              {passwordNotice && <Banner tone="success">{passwordNotice}</Banner>}
              <InlineStack>
                <Button
                  variant="primary"
                  loading={savingPassword}
                  disabled={!currentPassword || !newPassword || !confirmPassword}
                  onClick={changePassword}
                >
                  Change password
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default Profile;
